/**
 * Converte os filtros do frontend para o formato esperado pela API
 * @param {Object} filtros - Filtros selecionados no frontend
 * @returns {Object} Filtros formatados para a API
 */
export const construirFiltrosAPI = (filtros) => {
  const filtrosAPI = {};
  
  // Função auxiliar para verificar se um valor é válido (não vazio)
  const isValidValue = (value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value && value !== '';
  };
  
  // Função auxiliar para formatar valor (array vira string separada por |||)
  // Usamos ||| como separador porque os valores podem conter vírgulas
  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.join('|||');
    }
    return value;
  };
  
  // Só adiciona filtros que têm valor (não vazios)
  // Suporta tanto valores únicos quanto arrays (múltipla seleção)
  if (isValidValue(filtros?.estado)) {
    filtrosAPI['visao_geral.estado'] = formatValue(filtros.estado);
  }
  if (isValidValue(filtros?.cidade)) {
    filtrosAPI['visao_geral.cidade'] = formatValue(filtros.cidade);
  }
  if (isValidValue(filtros?.posto)) {
    filtrosAPI['visao_geral.posto'] = formatValue(filtros.posto);
  }
  if (isValidValue(filtros?.produto)) {
    filtrosAPI['visao_geral.nome_produto'] = formatValue(filtros.produto);
  }
  if (isValidValue(filtros?.funcionario)) {
    filtrosAPI['visao_geral.funcionario'] = formatValue(filtros.funcionario);
  }
  
  // Filtros cruzados adicionais
  if (isValidValue(filtros?.turno)) {
    filtrosAPI['visao_geral.turno'] = formatValue(filtros.turno);
  }
  if (isValidValue(filtros?.forma_pagamento)) {
    filtrosAPI['visao_geral.nome_forma_pagamento'] = formatValue(filtros.forma_pagamento);
  }
  if (isValidValue(filtros?.ano)) {
    // Para anos, converter diretamente para vírgula (anos são números, não precisam de |||)
    // O Looker aceita múltiplos anos separados por vírgula: "2025,2026"
    if (Array.isArray(filtros.ano)) {
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_year'] = filtros.ano.join(',');
    } else if (typeof filtros.ano === 'string' && filtros.ano.includes('|||')) {
      // Se já veio como string com |||, converter para vírgula
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_year'] = filtros.ano.split('|||').join(',');
    } else {
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_year'] = filtros.ano;
    }
  }
  
  // IMPORTANTE: Processar filtro de data do filtro cruzado PRIMEIRO (prioridade)
  // Isso garante que quando um filtro cruzado de data é aplicado, ele sobrescreve o filtro de período
  if (filtros?.['visao_geral.data_hora_abastecimento_sp_date'] && 
      filtros['visao_geral.data_hora_abastecimento_sp_date'] !== '') {
    const dataFiltroCruzado = filtros['visao_geral.data_hora_abastecimento_sp_date'];
    
    // Se for um mês (YYYY-MM), converter para intervalo do mês
    if (/^\d{4}-\d{2}$/.test(dataFiltroCruzado)) {
      // Formato YYYY-MM - converter para intervalo do mês
      const [ano, mes] = dataFiltroCruzado.split('-');
      const primeiroDia = `${ano}-${mes}-01`;
      // Último dia do mês: usar dia 0 do próximo mês
      // Em JS, meses são 0-indexed, então mês "05" (maio) = índice 4
      // Para obter último dia de maio, usar new Date(ano, 5, 0) = último dia de abril
      // Correto: new Date(ano, 6, 0) = último dia de maio
      const mesNum = parseInt(mes); // 1-12 (formato humano)
      const ultimoDia = new Date(parseInt(ano), mesNum, 0).toISOString().split('T')[0];
      const intervalo = `${primeiroDia} to ${ultimoDia}`;
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = intervalo;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(dataFiltroCruzado)) {
      // Se for um dia específico (YYYY-MM-DD), usar diretamente
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = dataFiltroCruzado;
    } else {
      // Para outros formatos (intervalo ou outros), usar diretamente
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = dataFiltroCruzado;
    }
  }
  // Manter compatibilidade com filtro de data antigo (se ainda existir e não houver filtro cruzado)
  else if (filtros?.data && filtros.data !== '' && !filtrosAPI['visao_geral.data_hora_abastecimento_sp_date']) {
    filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = filtros.data;
  }
  // Filtro de período usando data_min e data_max (só se não houver filtro cruzado de data)
  else if (filtros?.periodo && filtros.periodo !== '' && !filtrosAPI['visao_geral.data_hora_abastecimento_sp_date']) {
    const dataMin = filtros?.dataMin && filtros.dataMin !== '' ? filtros.dataMin : null;
    const dataMax = filtros?.dataMax && filtros.dataMax !== '' ? filtros.dataMax : null;
    
    // Construir filtro de data para o Looker
    // O Looker aceita apenas formatos simples: YYYY-MM-DD, >=YYYY-MM-DD, ou <=YYYY-MM-DD
    if (dataMin && dataMax) {
      // Se temos ambas as datas e são iguais, usar apenas uma data
      if (dataMin === dataMax) {
        filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = dataMin;
      } else {
        // Para intervalo, enviar ambas as datas separadamente para processamento no backend
        // O backend irá criar um filter_expression para combinar >= e <=
        filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = `${dataMin} to ${dataMax}`;
      }
    } else if (dataMin) {
      // Se só temos data mínima, usar formato >=
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = `>=${dataMin}`;
    } else if (dataMax) {
      // Se só temos data máxima, usar formato <=
      filtrosAPI['visao_geral.data_hora_abastecimento_sp_date'] = `<=${dataMax}`;
    }
  }

  return filtrosAPI;
};

